<template>
  <div class="contact-card-single">
    <Upgrade-Popup
      v-if="showPopup"
      :upgradePopupType="upgradePopupType"
      v-on:clickedOutside="showPopup = false"
    />
    <Credit-Limit-Popup
      v-if="showCreditLimitPopup"
      v-on:clickedOutside="showCreditLimitPopup = false"
    />
    <Review-Popup
      v-if="showReviewPopup"
      v-on:reviewPopupClosed="showReviewPopup = false"
    />
    <Activation-Popup
      v-if="showActivationPopup"
      v-on:closed="showActivationPopup = false"
    />
    <div v-if="profileData == null" class="custom-form-section">
      <div class="custom-domain-text">Find anybody's email with a click</div>
      <form
        v-on:submit.prevent="customWebsiteSubmit"
        class="form-section"
        ref="formDomain"
      >
        <Input-Floating-Label
          ref="firstName"
          :autocomplete="'off'"
          :type="'text'"
          :required="'true'"
          :id="'firstName'"
          :placeholder="'First Name'"
        />
        <Input-Floating-Label
          ref="lastName"
          :autocomplete="'off'"
          :type="'text'"
          :id="'lastName'"
          :pattern="'(.+)'"
          :required="'true'"
          :placeholder="'Last Name'"
        />
        <Input-Floating-Label
          ref="domain"
          :autocomplete="'off'"
          :type="'text'"
          :required="'true'"
          :id="'domain'"
          :pattern="'(.+[.?].+)'"
          :title="'Please enter valid domain'"
          :placeholder="'Company Domain'"
        />
        <div class="btn-section d-flex">
          <input :class="'btn-blue'" :value="'Get Contact'" :type="'submit'" />
        </div>
      </form>
    </div>
    <template v-else>
      <div v-if="!loading && profileData">
        <div class="user-info d-flex animate-fadein">
          <div class="top-section">
            <div class="name float-l width-100" v-if="profileData.fullName">
              {{ profileData.fullName }}
            </div>
            <div class="title float-l width-100" v-if="profileData.title">
              {{ profileData.title }}
            </div>
          </div>
          <Spinner v-if="loadingSpinnerEmail" />
          <div class="user-email-section" v-else>
            <div
              class="flex-c get-email-btn"
              v-if="!profileData.email && !errorMsg.msg && !errorMsg.butText"
            >
              <V-Button
                :callback="findEmailSmtp"
                :text="'Find email'"
                :loading="false"
              />
            </div>

            <div
              v-if="!profileData.email && errorMsg"
              class="diff-domain-section"
            >
              <span v-if="errorMsg.msg" class="errorMsg">
                {{ errorMsg.msg }}
              </span>
              <span
                v-if="errorMsg.butText"
                class="diff-domain-btn-section flex-c no-domain"
              >
                <Add-Domain-Find-Email-But
                  class="flex-c"
                  :text="errorMsg.butText"
                  :type="'domain'"
                  :data="profileData"
                  v-on:newDomainSubmitted="findEmailWithCustomDomain"
                />
              </span>
            </div>

            <div class="d-flex float-l width-100 email-section animate-fadein">
              <div class="flex-c email-hldr" v-if="profileData.email">
                <div class="email-info">
                  <Email-Icon viewBox="2 5 22 12" width="17px" height="13px" />
                  <span
                    :class="[{ cursor: !canShowButton() }, 'email noselect']"
                    @click="
                      copyClipboard(
                        profileData.masterContactId,
                        profileData.email,
                        'email'
                      )
                    "
                    >{{ profileData.email }}</span
                  >
                  <div class="copy-section flex-c" v-if="!canShowButton()">
                    <input
                      type="text"
                      :value="profileData.email"
                      :id="profileData.masterContactId + 'email'"
                      class="noVisibility"
                    />
                    <Copy-Icon
                      class="copy-icon"
                      @click="
                        copyClipboard(
                          profileData.masterContactId,
                          profileData.email,
                          'email'
                        )
                      "
                    />
                  </div>
                </div>
                <div
                  class="email-score"
                  v-if="profileData.emailConfidenceScore && !canShowButton()"
                >
                  <Email-confidence-card
                    class="fn-14"
                    :score="profileData.emailConfidenceScore"
                  />
                </div>
              </div>

              <div
                class="flex-c get-email-btn"
                v-if="profileData.email && !profileData.contactAlreadyOwned"
              >
                <Find-Email-Button
                  :masterContactId="profileData.masterContactId"
                  :trigger="false"
                  v-on:response="updatePhoneAndEmail"
                  :showUpgradePopup="showUpgradePopup"
                  :enableReviewPopup="enableReviewPopup"
                  :showActivationPopup="showActivationError"
                />
              </div>
              <div
                class="diff-domain-btn-section flex-c no-domain"
                v-if="
                  profileData.email && profileData.contactAlreadyOwned == true
                "
              >
                <Add-Domain-Find-Email-But
                  class="flex-c"
                  :text="'Try for a different domain'"
                  :type="'domain'"
                  :data="profileData"
                  v-on:newDomainSubmitted="findEmailWithCustomDomain"
                />
              </div>
            </div>
          </div>
          <div class="crm-section" v-if="canShowCRMButton()">
            <Crm-Export
              v-if="profileData.email"
              :userEmail="profileData.email"
              v-on:crmUpgradePopup="showUpgradePopup"
              :contactId="profileData.masterContactId"
              :pageType="'domain'"
            />
          </div>
          <div
            class="user-detail-section d-flex"
            v-if="profileData.level || profileData.department"
          >
            <div
              class="level flex-c float-l width-100"
              v-if="profileData.level"
            >
              <Decision-Maker />
              <span>{{ getLevelNameFromLevel(profileData.level) }}</span>
            </div>
            <div
              class="portfolio flex-c float-l width-100"
              v-if="profileData.department"
            >
              <Portfolio />
              <span>{{ profileData.department.join(', ') }}</span>
            </div>
            <div v-if="profileData.phone" class="phone-info d-flex width-100">
              <div
                class="txt-blue flex-c"
                v-for="(ele, index) in profileData.phone"
                :key="index"
              >
                <Phone-Card v-if="ele.score == 'high'" :phone="ele" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="placeholder top">
        <div class="top-section">
          <div class="flex-c email-hldr">
            <div class="email-info linear-background"></div>
            <div class="email-score linear-background">
              <Email-confidence-card :loading="loading" />
            </div>
          </div>
          <div class="diff-domain-btn-section">
            <div class="linear-background"></div>
            <div class="linear-background"></div>
          </div>
        </div>
        <div class="user-info d-flex">
          <div class="width-70 left-section">
            <div class="name linear-background"></div>
            <div class="title linear-background"></div>
            <div class="level linear-background"></div>
            <div class="portfolio linear-background"></div>
            <div class="phone-info linear-background"></div>
          </div>
          <!-- <div class="width-30 but-holder add-salesforce">
            <div class="linear-background"></div>
          </div>-->
        </div>
      </div>
      <div v-if="!loadingCompany">
        <div
          class="view-all txt-blue"
          v-if="conatactCount && companyData.primaryDomain"
          @click="showAllOtherContact"
          :data-link="companyData.primaryDomain"
        >
          View {{ conatactCount | formatNumber }}
          {{ conatactCount | singularPlural('contact') }} from
          {{ companyData.name }}
        </div>

        <div
          class="company-info d-flex animate-fadein"
          v-if="!loadingCompany && companyData"
        >
          <div class="decision-link-head" v-if="companyData.name">
            {{ companyData.name }}
          </div>
          <!-- <div class="multi-item"> -->
          <div
            class="decision-link d-flex float-l width-100 txt-blue"
            v-if="companyData.primaryDomain && companyData.website"
          >
            <Decision-Link />
            <a :href="companyData.website" target="_blank" class="txt-blue">
              {{ companyData.primaryDomain }}
            </a>
          </div>
          <div
            class="company-count d-flex float-l width-100"
            v-if="companyData.employeeCount"
          >
            <Company-Count />
            <span>{{ companyData.employeeCount }}</span>
          </div>
          <div
            class="revenue d-flex float-l width-100"
            v-if="companyData.revenue"
          >
            <Revenue />
            <span>{{ companyData.revenue }}</span>
          </div>
          <!-- </div> -->
          <div
            class="industry d-flex float-l width-100"
            v-if="companyData.industry"
          >
            <Industry-Icon />
            <span>{{ companyData.industry }}</span>
          </div>
          <div
            class="location d-flex float-l width-100"
            v-if="
              companyData.street ||
              companyData.city ||
              companyData.state ||
              companyData.country
            "
          >
            <Location />
            <span
              v-html="
                formatLocationString(
                  companyData.street,
                  companyData.city,
                  companyData.state,
                  companyData.country
                )
              "
            ></span>
          </div>
        </div>
      </div>
      <div v-else class="placeholder bottom">
        <div class="company-info d-flex">
          <div class="decision-link-head linear-background"></div>
          <div class="multi-item">
            <div class="decision-link linear-background"></div>
            <div class="company-count linear-background"></div>
            <div class="revenue linear-background"></div>
          </div>
          <div class="industry linear-background"></div>
          <div class="location linear-background"></div>
          <div class="view-all linear-background"></div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import PhoneCard from './CustomWebsitePhoneCard'
import emailConfidenceCard from '@/components/EmailConfidenceCard.vue'
import findEmailButton from '@/components/FindEmailButton.vue'
import Button from '@/components/Button.vue'
import UpgradePopup from '@/components/UpgradePopup.vue'
import ReviewPopup from '@/components/ReviewPopup.vue'
import ActivationPopup from '@/components/ActivationPopup.vue'
import sharedFunctions from '@/assets/util/functions.js'

import emailIcon from '@/assets/icons/svg/email.svg'

import { post, get } from '@/assets/util/rest.js'
import { advanceSearchUrl } from '@/assets/util/constants/constants.js'

import addDomainFindEmail from '@/components/AddDomainFindEmail'
import decisionIcon from '@/assets/icons/svg/decision-making-svgrepo-com-1.svg'
import portfolioIcon from '@/assets/icons/svg/portfolio.svg'
import decisionLinkIcon from '@/assets/icons/svg/grid-world-1.svg'
import companyCountIcon from '@/assets/icons/svg/group-2.svg'
import revenueIcon from '@/assets/icons/svg/coin-1.svg'
import locationIcon from '@/assets/icons/svg/placeholder-3.svg'

import copyIcon from '@/assets/icons/svg/copy.svg'
import industryIcon from '@/assets/icons/svg/factory.svg'
import Spinner from '@/components/Spinner'
import InputFloatingLabel from '@/components/InputFloatingLabel'
import CreditLimitPopup from '@/components/CreditLimitPopup.vue'
// import { MxEvent } from '@/assets/util/constants/analytics'
// import CrmExportLoader from '@/components/crmExport/CrmExportLoader.vue'
// import CrmExport from '@/components/crmExport/CrmExport.vue'

export default {
  name: 'CustomWebsiteContactCard',
  props: [
    'profileData',
    'loading',
    'companyData',
    'conatactCount',
    'loadingCompany',
    'error',
  ],
  watch: {
    error: {
      handler(newValue, oldValue) {
        if (oldValue != newValue) {
          this.errorMsg = newValue
        }
      },
    },
    profileData: {
      handler() {
        this.errorMsg = { msg: null }
      },
    },
  },
  data: function () {
    return {
      trySMPT: false,
      addButtonLoading: false,
      smtpCompleted: false,
      showPopup: false,
      showCreditLimitPopup: false,
      errorMsg: {
        msg: null,
      },
      upgradePopupType: null,
      showReviewPopup: false,
      showActivationPopup: false,
      loadingSpinnerEmail: false,
    }
  },
  computed: {
    isContactOwned: function () {
      const id = this.profileData.linkedinId
      return this.$store.state.linkedin.bulk.contacts[id].contactAlreadyOwned
    },
  },
  components: {
    'Phone-Card': PhoneCard,
    'Email-confidence-card': emailConfidenceCard,
    'Email-Icon': emailIcon,
    'Add-Domain-Find-Email-But': addDomainFindEmail,
    'Decision-Maker': decisionIcon,
    Portfolio: portfolioIcon,
    'Decision-Link': decisionLinkIcon,
    'Company-Count': companyCountIcon,
    Revenue: revenueIcon,
    Location: locationIcon,
    'Find-Email-Button': findEmailButton,
    'V-Button': Button,
    'Copy-Icon': copyIcon,
    'Industry-Icon': industryIcon,
    'Upgrade-Popup': UpgradePopup,
    'Review-Popup': ReviewPopup,
    'Activation-Popup': ActivationPopup,
    Spinner: Spinner,
    'Input-Floating-Label': InputFloatingLabel,
    'Credit-Limit-Popup': CreditLimitPopup,
    // 'Crm-Export': CrmExport,
    // 'Crm-Export-Loader': CrmExportLoader
  },
  methods: {
    updatePhoneAndEmail(res) {
      this.profileData.email = res.email
      this.profileData.phone = res.phone
      this.profileData.mcId = res.masterContactId
      this.profileData.contactAlreadyOwned = true
    },
    findEmailSmtp() {
      const filteredData = sharedFunctions.getFilteredData(this.profileData)
      this.findEmail(filteredData)
    },
    findEmailWithCustomDomain(inputData) {
      this.profileData.domain = inputData.domain
      this.profileData.companyName = inputData.companyName
      this.profileData.title = null
      this.profileData.level = null
      this.profileData.email = null
      this.profileData.phone = null
      this.profileData.department = null
      this.companyData = null
      this.errorMsg.msg = null
      let customData = {}
      customData.fullName = this.profileData.fullName
      customData['experience'] = [
        {
          isVerifiedDomain: false,
          isActivelyWorking: true,
          domain: inputData.domain,
          companyName: inputData.companyName,
        },
      ]
      this.findEmail(customData)
    },
    doNothing() {},
    getLevelNameFromLevel(level) {
      let levelName = null
      if ('C-Level' === level || 'VP-Level' === level) {
        levelName = 'Decision maker'
      } else if ('Director-Level' === level) {
        levelName = 'Influencer'
      } else if ('Manager-Level' === level) {
        levelName = 'Champion'
      } else {
        levelName = level
      }
      return levelName
    },
    canShowCRMButton() {
      return (
        this.profileData &&
        this.profileData.email &&
        this.profileData.contactAlreadyOwned == true
      )
    },
    canShowButton() {
      return (
        this.profileData.domain &&
        (!this.profileData.email || this.profileData.email.indexOf('*') != -1)
      )
    },
    findEmail(data) {
      if (data) {
        this.loadingSpinnerEmail = true
        let that = this
        post('lp/email.htm', data)
          .then((response) => {
            setTimeout(function () {
              that.loadingSpinnerEmail = false
              if (response.status == 200) {
                response.json().then((res) => {
                  if (res.statusCode == '200') {
                    that.profileData = res.response
                    that.profileData.contactAlreadyOwned = true
                    let companyId =
                      res.response && res.response.companyId
                        ? res.response.companyId
                        : ''
                    if (companyId != '') {
                      that.getCompanyData(companyId)
                      that.getContactCount(companyId)
                    }
                  } else if (res.statusCode == '400') {
                    that.showActivationError()
                  } else if (res.statusCode == '402') {
                    that.showUpgradePopup('views')
                  } else if (res.statusCode == '420') {
                    that.setShowCreditLimitPopup()
                  } else {
                    that.profileData ? (that.profileData.email = null) : ''
                    console.log('respons msgs :' + res)
                    if (res.error && res.error[0] && res.error[0].code) {
                      that.errorMsg = that.getErrorMsg(res)
                    } else {
                      that.errorMsg = { msg: res.errorMessage }
                    }
                  }
                })
              }
            }, 2000)
          })
          .catch((e) => {
            that.loadingSpinnerEmail = false
            console.log('error: ' + e)
          })
      }
    },
    copyClipboard(cId, mail, name) {
      if (!this.canShowButton()) {
        let txtDisplay = 'Copied'
        const dataMap = {
          pageType: 'Try Different Domain',
        }
        sharedFunctions.clickToCopy(
          cId,
          mail,
          name,
          null,
          dataMap,
          txtDisplay,
          this.$store.state
        )
      }
    },
    showAllOtherContact(e) {
      let domain =
        e && e.currentTarget ? e.currentTarget.getAttribute('data-link') : ''
      if (domain != '')
        window.open(
          advanceSearchUrl(domain, this.$store.state.header.uiVersion)
        )
    },
    getErrorMsg(res) {
      return sharedFunctions.getRespectiveErrorMsg(res)
    },
    salesForceSuccess(res) {
      console.log('salesForceSuccess ', res)
    },
    showUpgradePopup(type) {
      if (type === 'credit_limit') {
        this.setShowCreditLimitPopup()
      } else {
        this.upgradePopupType = type
        this.showPopup = true
      }
    },
    setShowCreditLimitPopup() {
      this.showCreditLimitPopup = true
    },
    showActivationError() {
      this.showActivationPopup = true
    },
    enableReviewPopup() {
      this.showReviewPopup = true
    },
    getCompanyData(compId) {
      this.loadingCompany = true
      get('lp/getCompany.htm?id=' + compId)
        .then((response) => {
          this.loadingCompany = false
          if (response.status == 200) {
            response
              .json()
              .then((res) => {
                console.log('comapny data ', res)
                this.companyData = res
                //console.log('data: ' + JSON.stringify(res))
              })
              .catch((e) => {
                this.loadingCompany = false
                console.log('err: ' + e)
              })
          } else {
            this.loadingCompany = false
            console.log('status: ' + response.status)
          }
        })
        .catch((e) => {
          this.loadingCompany = false
          console.log('error: ' + e)
        })
    },
    getContactCount(compId) {
      get('lp/company/' + compId + '/contactCount.htm')
        .then((response) => {
          if (response.status == 200) {
            response
              .json()
              .then((res) => {
                console.log('contact count ', res)
                this.conatactCount = res
                //console.log('data: ' + JSON.stringify(res))
              })
              .catch((e) => {
                console.log('err: ' + e)
              })
          } else {
            console.log('status: ' + response.status)
          }
        })
        .catch((e) => {
          console.log('error: ' + e)
        })
    },
    formatLocationString(street, city, state, country) {
      let str = ''
      str = str + (street ? street + (city ? ', </br> ' : '') : '')
      str = str + (city ? city + (state ? ',</br> ' : '') : '')
      str = str + (state ? state + (country ? ', </br>' : '') : '')
      str = str + (country ? country + '' : '')
      return str
    },
    customWebsiteSubmit() {
      this.profileData = {}
      let firstName = this.$refs.firstName.$refs.input.value
      let lastName = this.$refs.lastName.$refs.input.value
      let domain = this.$refs.domain.$refs.input.value

      this.profileData.fullName = firstName + ' ' + lastName
      const inputData = {
        domain: domain,
        companyName: '',
      }
      console.log('dataMap ', inputData)
      this.findEmailWithCustomDomain(inputData)
    },
  },
  created() {
    sharedFunctions.track(
      'AP - Custom Find Email Section Shown',
      null,
      this.$store.state
    )
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.contact-card-single {
  width: 100%;
  color: $grey-color;
  line-height: 26px;
  .top-section {
    display: flex;
    flex-direction: column;
    // box-shadow: 0px 2px 3px 0px #efefef;
    padding: 10px;
    position: relative;
  }
  a {
    text-decoration: none;
  }
  .name {
    color: #474542;
  }
  .icon-img-spacing {
    margin: 0 0 0 8px;
  }

  .no-company {
    border-radius: 18.5px;
    background-color: $orange-light;
    color: $orange;
    padding: 10px;
    font-size: 14px;
    width: fit-content;
    margin-left: -10px;
    margin-top: 5px;
    border: solid 1px #a4a9af;
    cursor: pointer;
    svg {
      fill: $orange;
    }
  }
  .email-info {
    display: flex;
    align-items: center;
    width: 80%;
    height: 46px;
    .copy-icon {
      fill: #8e9398;
      width: 12px;
      height: 13px;
      cursor: pointer;
    }
    .copy-section {
      position: relative;
      padding-left: 5px;
    }
    .copied-success {
      color: #484848;
      display: none;
      font-size: 11px;
      position: absolute;
      top: 16px;
      width: 40px;
      left: 8px;
    }
    .show {
      display: block;
    }
  }
  .company-name {
    padding: 15px 0 5px 0;
    span {
      @extend .icon-img-spacing;
      @extend .ellipsis;
      min-width: 120px;
      max-width: 180px;
    }
  }
  .placeholder {
    .linear-background {
      margin-top: 10px;
      min-height: 15px;
    }
    .email-info {
      min-width: 240px;
    }
    .email-score {
      min-width: 60px;
    }
    .diff-domain-btn-section div {
      min-width: 120px;
    }
    .user-info {
      padding: 20px;
    }
    .name {
      width: 150px;
    }
    .title {
      width: 250px;
    }
    .level {
      width: 180px;
    }
    .portfolio {
      width: 80px;
    }
    .phone-info {
      width: 180px;
    }
    .company-info {
      .decision-link-head {
        width: 180px;
        min-height: 15px;
        padding: 0;
      }
      .multi-item {
        margin-top: 5px;
      }
      .decision-link,
      .company-count,
      .revenue {
        width: 100px;
        min-height: 15px;
        margin-right: 10px;
      }
      .industry {
        width: 100px;
      }
      .location {
        width: 150px;
      }
    }
  }

  .user-info {
    flex-direction: column;
    .email-not-found-section::v-deep {
      align-items: normal;
      margin-top: 15px;
      .left-cont {
        width: fit-content;
        white-space: nowrap;
        padding-right: 20px;
        svg {
          width: 19px;
          height: 19px;
          fill: #ef3e15;
        }
      }
      .right-cont {
        white-space: normal;
      }
    }
    .add-salesforce::v-deep {
      min-width: fit-content;
      position: absolute;
      top: 16px;
      right: 15px;
      button {
        border-radius: 17.5px;
        border: solid 1px #4899fc;
        color: $primary-color;
        font-size: 12px;
        background-color: $white;
        padding: 7px 10px 6px;
        align-items: center;
      }
    }
    .user-email-section {
      background-color: #f6f6f6;
      svg {
        width: 15px;
        height: 15px;
        fill: $primary-color;
      }
      padding: 0 10px;
      .email-section {
        flex-direction: column;
      }
      .get-email-btn::v-deep {
        margin: 10px 0;
        button {
          border-radius: 20px;
          border-width: 1px;
          padding: 7px 15px;
        }
      }
      .diff-domain-section {
        .errorMsg {
          display: flex;
          font-size: 14px;
          color: red;
          line-height: 22px;
        }
      }
      .diff-domain-btn-section {
        display: flex;
        margin-bottom: 10px;
        div {
          margin-right: 9px;
        }
      }
      .no-domain {
        border-radius: 18.5px;
        color: #636a72;
        padding: 2px 10px;
        font-size: 14px;
        width: fit-content;
        margin-top: 5px;
        cursor: pointer;
        border: solid 1px #a4a9af;
        svg {
          fill: $orange;
        }
        line-height: 21px;
      }
      .email-score::v-deep {
        svg {
          display: none; //Todo
        }
      }
    }

    .user-detail-section {
      flex-direction: column;
      padding: 10px;
    }
    .level,
    .portfolio {
      font-size: 14px;
      padding: 3px 0;
      min-width: fit-content;

      border-bottom: 1px solid;
      border-color: rgba(59, 59, 59, 0.1);
      span {
        margin-left: 15px;
      }
      svg {
        width: 15px;
        height: 15px;
        fill: #575f68;
      }
    }
    .phone-info {
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      padding: 4px 0;
      grid-row-gap: 7px;
      grid-column-gap: 7px;
    }
    .phone-card::v-deep {
      color: #636a72;
      // margin-top: 15px;
      svg {
        width: 15px;
        height: 15px;
        fill: #575f68;
        display: flex;
      }
      i {
        font-size: 24px;
      }
    }
  }

  .company-info {
    background-color: #f6f6f6;
    padding: 10px;
    flex-direction: column;
    .decision-link-head {
      font-size: 14px;
      font-weight: normal;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.02;
      letter-spacing: normal;
      text-align: left;
      color: #474542;
      padding: 10px 0;
    }
    .multi-item {
      display: flex;
    }
    .decision-link,
    .company-count,
    .revenue,
    .industry,
    .location {
      min-width: fit-content;

      margin-top: 5px;
      font-size: 14px;
      align-items: self-start;
      span,
      a {
        margin-left: 15px;
        line-height: 21px;
      }
      svg {
        width: 15px;
        height: 15x;
        fill: #575f68;
      }
    }
    .multi-item,
    .industry,
    .location {
      margin-top: 15px;
    }
    .location {
      svg {
        margin-top: 3px;
      }
      span,
      a {
        margin-left: 13px;
        line-height: 23px;
      }
    }
  }
  .name {
    font-size: 16px;
    @extend .ellipsis;
  }
  .title {
    color: $grey-color;
    font-size: 14px;
    @extend .ellipsis;
  }
  .email-hldr {
    color: $primary-color;
    justify-content: space-between;
    svg {
      fill: $primary-color;
    }
    .email {
      cursor: pointer;
      @extend .icon-img-spacing;
      min-width: 120px;
      font-size: 14px;
      font-weight: 500;
      word-break: break-word;
      line-height: 19px;
    }
  }
  .c-grey {
    color: $grey-color;
  }
  .left-section {
    @extend .ellipsis;
  }
  .line-1-25 {
    line-height: 1.25;
  }
  .but-holder {
    align-items: start;
    justify-content: flex-end;
    button {
      width: 80px;
      height: 30px;
      border-radius: 3px;
    }
  }
}
.custom-form-section {
  padding: 25px 5px;
  .custom-domain-text {
    margin-left: 10px;
    font-size: 14px;
  }
  .form-section {
    width: 100%;
    .btn-section::v-deep {
      margin-top: 30px;
      overflow: hidden;
      button,
      input {
        width: 90px;
        padding: 10px 0px;
        margin: 0 5px;
        font-size: 16px;
        border: solid 1px #56a2f9;
        border-radius: 5px;
        background-color: $white;
      }
      input.btn-blue {
        background-color: $primary-color;
        color: $white;
        width: 100%;
        cursor: pointer;
      }
    }
  }
}
</style>
